import { useState, useEffect } from 'react';
import type { PriceData } from '../types/prices';
import './PriceTable.css';

const eur = (n: number, dec = 2) =>
  n.toLocaleString('nl-BE', { style: 'currency', currency: 'EUR', minimumFractionDigits: dec, maximumFractionDigits: dec });

type Tab = 'bars' | 'coins' | 'scrap';

export function PriceTable() {
  const [tab, setTab] = useState<Tab>('bars');
  const [data, setData] = useState<PriceData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/argentor');
        if (res.ok) {
          const json = await res.json() as { success: boolean } & PriceData;
          if (json.success && !cancelled) setData(json);
        }
      } catch { /* leave data null — table shows an error */ }
      finally { if (!cancelled) setIsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section id="prices" className="price-section">
      <div className="price-section__container">
        <div className="price-section__header">
          <h2 className="price-section__title">Onze Actuele Aankoopprijzen</h2>
          <p className="price-section__subtitle">Dit is wat wij betalen — prijzen worden automatisch bijgewerkt</p>
        </div>

        <div className="price-tabs">
          {(['bars', 'coins', 'scrap'] as Tab[]).map(t => (
            <button
              key={t}
              className={`price-tab ${tab === t ? 'price-tab--active' : ''}`}
              onClick={() => setTab(t)}
            >
              {t === 'bars' ? 'Goudstaven' : t === 'coins' ? 'Goudmunten' : 'Sloopgoud'}
            </button>
          ))}
        </div>

        <div className="price-table-wrapper">
          {isLoading ? (
            <div className="price-table__loading">Prijzen laden...</div>
          ) : !data ? (
            <div className="price-table__error">Kon prijzen niet laden. Probeer later opnieuw.</div>
          ) : (
            <>
              {tab === 'bars' && (
                <table className="price-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Gewicht</th>
                      <th>Onze Aankoopprijs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.bars.map(bar => (
                      <tr key={bar.name} className="price-row">
                        <td className="price-row__name">{bar.name}</td>
                        <td>{bar.weight}</td>
                        <td className="price-row__buy">{eur(bar.price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {tab === 'coins' && (
                data.coins.length === 0 ? (
                  <div className="price-table__empty">
                    Muntprijzen zijn tijdelijk niet beschikbaar.
                    Neem gerust contact op voor de actuele prijzen.
                  </div>
                ) : (
                  <table className="price-table">
                    <thead>
                      <tr>
                        <th>Munt</th>
                        <th>Goudgewicht</th>
                        <th>Onze Aankoopprijs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.coins.map(coin => (
                        <tr key={coin.name} className="price-row">
                          <td className="price-row__name">🪙 {coin.name}</td>
                          <td>{coin.weight}</td>
                          <td className="price-row__buy">{eur(coin.price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}

              {tab === 'scrap' && (
                <table className="price-table">
                  <thead>
                    <tr>
                      <th>Kwaliteit</th>
                      <th>Particulier / gram</th>
                      <th>Bedrijf / gram</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.scrap.map(grade => (
                      <tr key={grade.name} className="price-row">
                        <td className="price-row__name">{grade.name}</td>
                        <td className="price-row__buy">{eur(grade.particulier, 2)}</td>
                        <td className="price-row__buy">{eur(grade.bedrijf, 2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} className="price-table__footnote">
                        Aankoopprijzen zijn indicatief. Neem contact op voor de exacte prijs.
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </>
          )}
        </div>

        <p className="price-table__disclaimer">
          Alle aankoopprijzen zijn indicatief en exclusief BTW. Neem contact op voor de definitieve prijs.
        </p>
      </div>
    </section>
  );
}
